import * as React from 'react';

import { toZoned } from '@internationalized/date';
import {
  enumPaymentMethod,
  PaymentMethod,
} from '@rematter/frontend-lib-api-client';
import {
  Button,
  Col,
  DatePicker,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogMain,
  DialogOverlay,
  DialogTitle,
  formatMoney,
  Row,
  TextArea,
  TextField,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { payBillAtom } from '../../_graphql/mutationPayBill';
import { nextCheckNumberQueryAtom } from '../../_graphql/queryNextCheckNumber';
import {
  $accountAllocations,
  $achNumber,
  $accountGroups,
  $applyAllocationMethod,
  $canSubmit,
  $checkFacilityId,
  $checkMemo,
  $checkNumber,
  $facilityIDs,
  $financialAccountId,
  $groupWireNumbers,
  $hasMixedCurrencies,
  $initPaymentItems,
  $isSubmitting,
  $itemAllocations,
  $paymentAmount,
  $paymentMethod,
  $resetPaymentAllocation,
  $selectedAllocationMethod,
  $showAllocationBanner,
  $transactionDate,
  $updateAccountAllocation,
  $updateAccountSelection,
  $updateGroupWireNumber,
  $updateItemAllocation,
  $updatePaymentMethod,
  $wireNumber,
  PAYMENT_METHOD_ACCOUNT_TYPES,
} from '../../_state/paymentAllocation.state';
import { useTranslations } from '../../i18n/useTranslations';
import { getCheckNumberForGroup, submitPaymentGroups } from '../../utils';
import { AccountGroupCard } from '../_shared/AccountGroupCard';
import { AllocationBanner } from '../_shared/AllocationBanner';

import { BillRecord } from '~/modules/finance/types';
import { SelectEnum } from '~/shared/components/select-fields/SelectEnum';
import { SelectFacility } from '~/shared/components/select-fields/SelectFacility';
import { SelectFinancialAccount } from '~/shared/components/select-fields/SelectFinancialAccount';
import { store } from '~/shared/state';
import { $localization } from '~/shared/state/preferences.state';

interface BillPaymentDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  bills: BillRecord[];
  onSuccess?: () => void;
}

//
// ⚛️ ⚛️ ⚛️ BILL PAYMENT DRAWER ⚛️ ⚛️ ⚛️
//
export const BillPaymentDrawer = ({
  isOpen,
  onClose,
  bills,
  onSuccess,
}: BillPaymentDrawerProps) => {
  // Translations
  const t = useTranslations();

  // Atoms
  const paymentMethod = useAtomValue($paymentMethod);
  const transactionDate = useAtomValue($transactionDate);
  const financialAccountId = useAtomValue($financialAccountId);
  const checkFacilityId = useAtomValue($checkFacilityId);
  const checkNumber = useAtomValue($checkNumber);
  const checkMemo = useAtomValue($checkMemo);
  const achNumber = useAtomValue($achNumber);
  const wireNumber = useAtomValue($wireNumber);
  const groupWireNumbers = useAtomValue($groupWireNumbers);
  const isSubmitting = useAtomValue($isSubmitting);
  const selectedAllocationMethod = useAtomValue($selectedAllocationMethod);
  const showAllocationBanner = useAtomValue($showAllocationBanner);
  const hasMixedCurrencies = useAtomValue($hasMixedCurrencies);
  const accountGroups = useAtomValue($accountGroups);
  const facilityIDs = useAtomValue($facilityIDs);
  const itemAllocations = useAtomValue($itemAllocations);
  const accountAllocations = useAtomValue($accountAllocations);
  const paymentAmount = useAtomValue($paymentAmount);
  const canSubmit = useAtomValue($canSubmit);
  const setTransactionDate = useSetAtom($transactionDate);
  const setCheckFacilityId = useSetAtom($checkFacilityId);
  const setCheckNumber = useSetAtom($checkNumber);
  const setCheckMemo = useSetAtom($checkMemo);
  const setAchNumber = useSetAtom($achNumber);
  const setWireNumber = useSetAtom($wireNumber);
  const setIsSubmitting = useSetAtom($isSubmitting);
  const setShowAllocationBanner = useSetAtom($showAllocationBanner);
  const setFinancialAccountId = useSetAtom($financialAccountId);
  const setItemAllocations = useSetAtom($itemAllocations);
  const initPaymentItems = useSetAtom($initPaymentItems);
  const resetPaymentAllocation = useSetAtom($resetPaymentAllocation);
  const updatePaymentMethod = useSetAtom($updatePaymentMethod);
  const updateAccountSelection = useSetAtom($updateAccountSelection);
  const updateItemAllocation = useSetAtom($updateItemAllocation);
  const updateAccountAllocation = useSetAtom($updateAccountAllocation);
  const updateGroupWireNumber = useSetAtom($updateGroupWireNumber);
  const applyAllocationMethod = useSetAtom($applyAllocationMethod);

  // Data hooks
  const toast = useToast();
  const payBill = useSetAtom(payBillAtom());
  const isCheckPaymentForQuery =
    paymentMethod === 'Check' && !!financialAccountId;
  const $nextCheckNumber = React.useMemo(
    () =>
      nextCheckNumberQueryAtom(() => ({
        variables: {
          financialAccountId: financialAccountId ?? '',
        },
        skip: !isCheckPaymentForQuery,
      })),
    [financialAccountId, isCheckPaymentForQuery],
  );
  const { data: nextCheckNumber } = useAtomValue($nextCheckNumber);

  // Callbacks
  const handleClose = React.useCallback(() => {
    resetPaymentAllocation();
    onClose();
  }, [resetPaymentAllocation, onClose]);

  const handleSubmit = React.useCallback(async () => {
    if (
      !bills.length ||
      !transactionDate ||
      paymentAmount <= 0 ||
      !financialAccountId
    )
      return;

    setIsSubmitting(true);
    try {
      const zonedDateTime = toZoned(
        transactionDate,
        store.get($localization).timezone ?? 'UTC',
      );
      const isMultiGroup = accountGroups.length > 1;

      await submitPaymentGroups({
        accountGroups,
        itemAllocations,
        buildMutation: (group, i) =>
          payBill({
            accountId:
              group.accountKey === '__no_account__'
                ? undefined
                : group.accountKey,
            bills: group.items
              .map((bill) => ({
                billId: bill.id,
                appliedAmount: {
                  amount: itemAllocations.get(bill.id) ?? 0,
                  currencyCode: bill.amount.currencyCode,
                },
              }))
              .filter((item) => item.appliedAmount.amount > 0),
            paymentMethod,
            financialAccountId,
            transactionDate: zonedDateTime,
            checkFacilityId: checkFacilityId || undefined,
            checkNumber: getCheckNumberForGroup(checkNumber, i) || undefined,
            checkMemo: checkMemo || undefined,
            achNumber: achNumber || undefined,
            wireNumber:
              (isMultiGroup
                ? groupWireNumbers.get(group.accountKey)
                : wireNumber) || undefined,
          }),
        onAllSuccess: () => {
          toast.success(t.bills.paymentDrawer.messages.success);
          onSuccess?.();
          handleClose();
        },
        onPartialSuccess: (succeeded, total, fulfilledGroups) => {
          toast.warning(
            t.bills.paymentDrawer.messages.partialSuccess(succeeded, total),
          );
          setItemAllocations((prev) => {
            const next = new Map(prev);
            for (const group of fulfilledGroups) {
              for (const item of group.items) next.set(item.id, 0);
            }
            return next;
          });
          onSuccess?.();
        },
        onAllFailed: (error) => {
          toast.error(
            t.bills.paymentDrawer.messages.error(
              error instanceof Error
                ? error.message
                : t.messages.common.unknownError,
            ),
          );
        },
      });
    } catch (error) {
      toast.error(
        t.bills.paymentDrawer.messages.error(
          error instanceof Error
            ? error.message
            : t.messages.common.unknownError,
        ),
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [
    bills,
    transactionDate,
    paymentAmount,
    accountGroups,
    itemAllocations,
    paymentMethod,
    financialAccountId,
    checkFacilityId,
    checkNumber,
    checkMemo,
    achNumber,
    wireNumber,
    groupWireNumbers,
    setIsSubmitting,
    setItemAllocations,
    payBill,
    toast,
    onSuccess,
    handleClose,
    t,
  ]);

  // Effects
  /** Initialize payment items when drawer opens or bills change */
  React.useEffect(() => {
    if (isOpen && bills.length > 0) {
      initPaymentItems({
        items: bills.map((bill) => ({
          ...bill,
          amount: bill.openAmount ?? bill.amount,
        })),
        unknownLabel: t.bills.list.groupHeader.unknownAccount,
      });
    }
  }, [
    isOpen,
    bills,
    initPaymentItems,
    t.bills.list.groupHeader.unknownAccount,
  ]);

  /** Auto-fill check number from next available */
  React.useEffect(() => {
    if (isCheckPaymentForQuery && nextCheckNumber) {
      setCheckNumber(nextCheckNumber);
    }
  }, [isCheckPaymentForQuery, nextCheckNumber, setCheckNumber]);

  if (!isOpen || !bills.length) {
    return null;
  }

  const isMultipleBills = bills.length > 1;
  const isCheckPayment = paymentMethod === 'Check';
  const isAchPayment = paymentMethod === 'ACH';
  const isMultiGroup = accountGroups.length > 1;
  const isWirePayment = paymentMethod === 'Wire';

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={onClose}
      className="@BillPaymentDrawer"
    >
      <DialogContent
        position="right"
        dialogClassName="w-96 grid-rows-[auto_1fr_auto]"
        className="@BillPaymentDrawer"
        isKeyboardDismissDisabled
      >
        <DialogHeader>
          <DialogTitle>
            {isMultipleBills
              ? t.bills.paymentDrawer.title.multiple
              : t.bills.paymentDrawer.title.single}
          </DialogTitle>
        </DialogHeader>

        <DialogMain>
          <Col gap4>
            <SelectEnum<PaymentMethod>
              label={t.bills.paymentDrawer.fields.paymentMethod}
              enumValue={enumPaymentMethod}
              value={paymentMethod}
              onChange={updatePaymentMethod}
              isRequired
            />

            <DatePicker
              label={t.bills.paymentDrawer.fields.transactionDate}
              value={transactionDate ?? undefined}
              onChange={setTransactionDate}
              isRequired
            />

            {isCheckPayment && (
              <>
                <SelectFacility
                  label={t.bills.paymentDrawer.fields.checkFacility}
                  value={checkFacilityId ?? undefined}
                  onChange={(value) => setCheckFacilityId(value ?? null)}
                />
                <TextField
                  label={
                    isMultiGroup
                      ? t.bills.paymentDrawer.fields.startingCheckNumber
                      : t.bills.paymentDrawer.fields.checkNumber
                  }
                  value={checkNumber}
                  onChange={setCheckNumber}
                  description={
                    isMultiGroup && checkNumber
                      ? t.bills.paymentDrawer.messages.checkAutoIncrement(
                          checkNumber,
                          getCheckNumberForGroup(
                            checkNumber,
                            accountGroups.length - 1,
                          ) ?? '',
                        )
                      : undefined
                  }
                />
                <TextArea
                  label={t.bills.paymentDrawer.fields.checkMemo}
                  value={checkMemo}
                  onChange={setCheckMemo}
                  rows={2}
                />
              </>
            )}
            {isAchPayment && (
              <TextField
                label={t.bills.paymentDrawer.fields.achNumber}
                value={achNumber ?? ''}
                onChange={(value) => setAchNumber(value || null)}
                isRequired
              />
            )}
            {isWirePayment && !isMultiGroup && (
              <TextField
                label={t.bills.paymentDrawer.fields.wireNumber}
                value={wireNumber ?? ''}
                onChange={(value) => setWireNumber(value || null)}
              />
            )}

            <SelectFinancialAccount
              label={t.bills.paymentDrawer.fields.financialAccount}
              value={financialAccountId ?? undefined}
              onChange={(value: string | undefined) =>
                setFinancialAccountId(value ?? null)
              }
              visibleInPayments={true}
              facilityIDs={facilityIDs}
              types={PAYMENT_METHOD_ACCOUNT_TYPES[paymentMethod]}
              onAccountSelect={updateAccountSelection}
              isRequired
            />

            {showAllocationBanner && (
              <AllocationBanner
                variant="bill"
                selectedAllocationMethod={selectedAllocationMethod}
                onKeepCurrent={() => setShowAllocationBanner(false)}
                onApply={applyAllocationMethod}
              />
            )}

            {hasMixedCurrencies && (
              <div className="rounded bg-failure/10 px-3 py-2">
                <p className="text-sm text-failure">
                  {t.bills.paymentDrawer.messages.mixedCurrency}
                </p>
              </div>
            )}
          </Col>

          <Col gap4 className="mt-6">
            {accountGroups.map((group) => {
              const groupAllocation =
                accountAllocations.get(group.accountKey) ?? 0;
              const isPartial = groupAllocation < group.total;

              return (
                <AccountGroupCard
                  key={group.accountKey}
                  variant="bill"
                  group={group}
                  groupAllocation={groupAllocation}
                  showItemDetails={isPartial && group.items.length > 1}
                  isWirePayment={isWirePayment}
                  isMultiGroup={isMultiGroup}
                  groupWireNumbers={groupWireNumbers}
                  selectedAllocationMethod={selectedAllocationMethod}
                  itemAllocations={itemAllocations}
                  updateAccountAllocation={updateAccountAllocation}
                  updateGroupWireNumber={updateGroupWireNumber}
                  updateItemAllocation={updateItemAllocation}
                />
              );
            })}
            {isMultiGroup && (
              <Row xBetween className="border-t pt-2">
                <span className="text-sm font-semibold">
                  {t.bills.paymentDrawer.fields.totalAmount}
                </span>
                <span className="text-sm font-semibold">
                  {formatMoney(paymentAmount, bills[0].amount.currencyCode)}
                </span>
              </Row>
            )}
          </Col>
        </DialogMain>

        <DialogFooter>
          <Row xEnd gap3 className="mt-auto">
            <Button
              variant="outline"
              onPress={handleClose}
              isDisabled={isSubmitting}
            >
              {t.bills.paymentDrawer.buttons.cancel}
            </Button>
            <Button
              variant="solid"
              color="primary"
              onPress={handleSubmit}
              isPending={isSubmitting}
              isDisabled={!canSubmit}
            >
              {t.bills.paymentDrawer.buttons.pay}
            </Button>
          </Row>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
